import { useEffect, useRef, useState } from "react";
import {
  StyledAddModal,
  StyledAddModalInput,
  StyledAddModalList,
  StyledAddModalListContainer,
  StyledModalContainer,
} from "./index.styled";
import { Button, Form, Input, InputRef } from "antd";
import {
  messageBotChildRoles,
  messageBotNodeDataValues,
} from "@lib/constants/MessageBot";
import {
  MessageBotNode,
  MessageBotNodeData,
  MessageBotNodesEnum,
} from "@lib/types/MessageBot";
import { useTypedDispatch, useTypedSelector } from "@lib/redux";
import { addNode, setAddNodeModal } from "@lib/redux/slices/messageBot";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { uniqueId } from "lodash";
import { useIntl } from "react-intl";
import { IoArrowBackSharp } from "react-icons/io5";

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

export default function AddNodeModal() {
  const { messages } = useIntl();
  const dispatch = useTypedDispatch();
  const { isOpen = false, parentId = null } = useTypedSelector(
    (state) => state.messageBot?.addNodeModal,
  );
  const { nodes } = useTypedSelector((state) => state.messageBot);
  const [form] = Form.useForm();
  const inputRef = useRef<InputRef>(null);
  const [search, setSearch] = useState("");
  const [selectedNode, setSelectedNode] = useState<MessageBotNodesEnum | null>(
    null,
  );

  const parent = nodes.find((node) => node.id === parentId);

  useEffect(() => {
    if (isOpen) {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [inputRef.current, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setSelectedNode(null);
      form.resetFields();
    }
  }, [isOpen]);

  const handleCloseModal = () => {
    dispatch(setAddNodeModal(null));
  };

  const onFinish = (values: MessageBotNodeData<MessageBotNodesEnum>) => {
    const id = uniqueId("node_");
    const parent = nodes.find((node) => node.id === parentId);
    const nodeData: MessageBotNode = {
      id: id,
      type: selectedNode,
      data: values,
      position: { x: 0, y: 0 },
      parent: parent,
      generation: parent?.generation + 1,
    };
    dispatch(addNode(nodeData));
    handleCloseModal();
  };

  const nodeDataValues = messageBotNodeDataValues[selectedNode];

  return (
    <StyledAddModal centered open={isOpen} onCancel={handleCloseModal}>
      {selectedNode ? (
        <StyledModalContainer>
          <Form<MessageBotNodeData<MessageBotNodesEnum>>
            {...layout}
            onFinish={onFinish}
            form={form}
            autoComplete="off"
          >
            <Form.Item label="Parent">
              <Input disabled value={parent?.data?.label || "Root"} />
            </Form.Item>

            <Form.Item label="Type">
              <Input disabled value={selectedNode} />
            </Form.Item>

            {Object.entries(nodeDataValues).map(([key, value]) => {
              return (
                <Form.Item
                  label={
                    (messages[value.messageId] as string) || value.messageId
                  }
                  name={key}
                >
                  {value.type === "input" ? (
                    <Input />
                  ) : (
                    <Input.TextArea rows={4} />
                  )}
                </Form.Item>
              );
            })}

            <StyledFlex style={{ justifyContent: "flex-end", gap: "10px" }}>
              <Button onClick={() => setSelectedNode(null)}>
                <IoArrowBackSharp /> Previous
              </Button>
              <Button type="primary" htmlType="submit">
                Add Node
              </Button>
            </StyledFlex>
          </Form>
        </StyledModalContainer>
      ) : (
        <>
          <Form form={form}>
            <StyledAddModalInput
              ref={inputRef}
              autoFocus={true}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Node Name ..."
            />
          </Form>

          <StyledAddModalListContainer>
            <StyledAddModalList
              size="small"
              dataSource={messageBotChildRoles[parent?.type]?.filter((item) =>
                item.toLowerCase().includes(search.toLowerCase()),
              )}
              renderItem={(item: string, index: number) => (
                <StyledAddModalList.Item
                  key={index}
                  onClick={() => {
                    setSelectedNode(item as MessageBotNodesEnum);
                  }}
                >
                  <StyledAddModalList.Item.Meta
                    title={item}
                    description="This is the description"
                  />
                </StyledAddModalList.Item>
              )}
            />
          </StyledAddModalListContainer>
        </>
      )}
    </StyledAddModal>
  );
}
